import { useMutation } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { Button } from '@app/components/ui/button'
import { Input } from '@app/components/ui/input'
import { authKeys, login } from '@app/features/auth/api'
import { ApiError } from '@app/lib/api'
import { queryClient } from '@app/lib/query-client'

/**
 * Login screen — the only screen outside the AppShell: the sidebar and the top
 * bar are never visible before signing in.
 *
 * Authorization is still shallow: one coarse role per account (`admin`,
 * `purchasing` or `staff`, see worker/api/auth.contract.ts), no per-permission
 * grid and no password reset flow. Accounts are created with
 * `pnpm seed:user <e-posta> <ad> [parola] [--role admin|purchasing|staff]`
 * (see README).
 */
export function LoginScreen() {
  const navigate = useNavigate()
  const { redirect } = useSearch({ from: '/giris' })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: (user) => {
      /*
       * Drop everything the previous account left behind, exactly as logout
       * does (app-shell.tsx UserChip). Reaching this screen does NOT imply a
       * logout ran: an expired or revoked session sends the route guard here
       * with a client-side redirect, so on a shared office machine the cache
       * still holds the last user's payloads. `/api/dashboard/summary` is
       * role-dependent but its query key is not, so without this a staff login
       * would be served the previous admin's receivables and supplier exposure
       * straight out of the cache — and a `purchasing` login the receivables.
       *
       * Order matters: `clear()` after `setQueryData` would drop the session
       * entry the guard reads.
       */
      queryClient.clear()
      // So the guard (router.tsx beforeLoad) does not have to ask again.
      queryClient.setQueryData(authKeys.session, user)
      void navigate({ to: redirect ?? '/', replace: true })
    },
  })

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (loginMutation.isPending) return
    loginMutation.mutate({ email: email.trim(), password })
  }

  const error = loginMutation.error
    ? loginMutation.error instanceof ApiError
      ? loginMutation.error.message
      : 'Giriş yapılamadı. Bağlantınızı kontrol edip yeniden deneyin.'
    : null

  return (
    <main className="grid min-h-dvh place-items-center bg-kagit px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-5 flex items-center gap-2.5">
          <span
            aria-hidden
            className="grid size-7 shrink-0 place-items-center rounded-xs bg-kordovan font-display text-[0.9375rem] font-semibold text-kagit"
          >
            P
          </span>
          <span className="font-display text-xl font-semibold tracking-tight text-ink">Panel</span>
        </div>

        <section className="rounded-sm border border-hairline bg-surface">
          <header className="border-b border-hairline px-4 py-3">
            <h1 className="font-display text-base font-semibold tracking-tight text-ink">
              Oturum aç
            </h1>
            <p className="mt-0.5 text-micro text-ink-muted">
              Panel hesabınızın e-posta adresi ve parolası.
            </p>
          </header>

          <form className="flex flex-col gap-3 px-4 py-4" onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-1">
              <label htmlFor="email" className="text-micro font-medium text-ink-muted">
                E-posta
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                autoFocus
                required
                spellCheck={false}
                autoCapitalize="none"
                aria-invalid={error ? true : undefined}
                className="h-9"
                placeholder="ad@modelle.com"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="password" className="text-micro font-medium text-ink-muted">
                Parola
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                aria-invalid={error ? true : undefined}
                className="h-9"
              />
            </div>

            {error ? (
              <p
                role="alert"
                className="rounded-xs border border-kordovan/25 bg-kordovan/6 px-2.5 py-1.5 text-data text-kordovan"
              >
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              className="mt-1 h-9 w-full"
              disabled={loginMutation.isPending || !email || !password}
            >
              {loginMutation.isPending ? 'Giriş yapılıyor…' : 'Giriş yap'}
            </Button>
          </form>
        </section>

        <p className="mt-3 text-micro text-ink-muted">Panel yalnızca fabrika yönetimi içindir.</p>
      </div>
    </main>
  )
}
